import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import api from "api/axios";

const steps = ["Session", "Type", "Participants", "Récapitulatif"];

const participantVide = {
  est_conjoint: false,
  nom: "", prenom: "", date_naissance: "", num_passeport: "",
  date_expiration_passeport: "", telephone: "", email: "", sexe: "", nationalite: "",
  besoin_assistance: false, fauteuil_roulant: false, malade: false, malade_details: "",
  besoin_carte_sejour: false,
  tarif_id: "",
};

const fmt = (n) => parseFloat(n || 0).toLocaleString("fr-FR");

function FormulaireInscriptionOumrah({ open, onClose, editId, onSaved }) {
  const [sessions, setSessions] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [sessionId, setSessionId] = useState("");
  const [typeInscription, setTypeInscription] = useState("individuel");
  const [participants, setParticipants] = useState([{ ...participantVide }]);
  const [statutInscription, setStatutInscription] = useState(null);
  // Documents (chargés uniquement en édition)
  const [docsInscription, setDocsInscription] = useState(null);
  const [uploadEnCours, setUploadEnCours] = useState({}); // { "participantId_typeId": true }
  const [erreursPasseport, setErreursPasseport] = useState({}); // { index: message }

  const fetchSessions = async () => {
    try {
      const res = await api.get("/oumrah/sessions");
      setSessions(res.data.filter((s) => s.statut === "ouverte"));
    } catch (err) { console.error(err); }
  };

  const chargerPourEdition = async (id) => {
    try {
      const res = await api.get("/oumrah/inscriptions/" + id);
      const insc = res.data;
      setSessionId(insc.session_id);
      setTypeInscription(insc.type_inscription || "individuel");
      setStatutInscription(insc.statut || null);
      chargerDocuments(id);
      setParticipants(
        insc.participants.length
          ? insc.participants.map((p) => ({
              id: p.id,
              nom: p.nom || "",
              prenom: p.prenom || "",
              date_naissance: p.date_naissance ? p.date_naissance.slice(0, 10) : "",
              num_passeport: p.num_passeport || "",
              date_expiration_passeport: p.date_expiration_passeport ? p.date_expiration_passeport.slice(0, 10) : "",
              telephone: p.telephone || "",
              email: p.email || "",
              sexe: p.sexe || "",
              nationalite: p.nationalite || "",
              besoin_assistance: !!p.besoin_assistance,
              besoin_carte_sejour: !!p.besoin_carte_sejour,
              fauteuil_roulant: !!p.fauteuil_roulant,
              malade: !!p.malade,
              malade_details: p.malade_details || "",
              est_conjoint: !!p.conjoint_id,
              tarif_id: p.tarif_id || "",
            }))
          : [{ ...participantVide }]
      );
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors du chargement de l'inscription");
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchSessions();
    setActiveStep(0);
    setError("");
    if (editId) {
      chargerPourEdition(editId);
    } else {
      setSessionId("");
      setTypeInscription("individuel");
      setParticipants([{ ...participantVide }]);
      setStatutInscription(null);
      setDocsInscription(null);
      setUploadEnCours({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editId]);

  const sessionChoisie = sessions.find((s) => s.id === parseInt(sessionId));
  const tarifs = sessionChoisie?.tarifs || [];

  // Chargement de l'état des documents pour l'inscription en cours d'édition
  const chargerDocuments = async (inscriptionId) => {
    try {
      const res = await api.get("/oumrah/documents/inscription/" + inscriptionId);
      setDocsInscription(res.data);
    } catch (err) {
      console.error("Chargement documents:", err);
    }
  };

  // Rafraîchir la liste des documents après une action
  const rafraichirDocuments = () => {
    if (editId) chargerDocuments(editId);
  };

  // Upload d'un fichier pour un participant + type
  const uploaderFichier = async (participantId, typeDocId, file) => {
    if (!file) return;
    const key = participantId + "_" + typeDocId;
    setUploadEnCours((prev) => ({ ...prev, [key]: true }));
    try {
      const formData = new FormData();
      formData.append("fichier", file);
      await api.post(
        "/oumrah/participants/" + participantId + "/documents/" + typeDocId + "/upload",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      rafraichirDocuments();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de l'upload");
    } finally {
      setUploadEnCours((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  // Suppression d'un document
  const supprimerFichier = async (participantId, typeDocId) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await api.delete("/oumrah/participants/" + participantId + "/documents/" + typeDocId);
      rafraichirDocuments();
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  // Ouvrir/télécharger un fichier (dans un nouvel onglet)
  const ouvrirFichier = async (participantId, typeDocId) => {
    try {
      const res = await api.get(
        "/oumrah/participants/" + participantId + "/documents/" + typeDocId + "/fichier",
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      alert("Erreur lors de l'ouverture du fichier");
    }
  };

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  const majParticipant = (index, champ, valeur) => {
    const copie = [...participants];
    copie[index][champ] = valeur;
    setParticipants(copie);
  };
  const toggleConjoint = (index) => {
    const copie = [...participants];
    const dejaCoches = copie.filter((p, i) => p.est_conjoint && i !== index).length;
    if (!copie[index].est_conjoint && dejaCoches >= 2) {
      alert("Vous ne pouvez désigner que 2 personnes comme couple marié.");
      return;
    }
    copie[index].est_conjoint = !copie[index].est_conjoint;
    setParticipants(copie);
  };

  const ajouterParticipant = () => setParticipants([...participants, { ...participantVide }]);
  const retirerParticipant = (index) => {
    const copie = participants.filter((_, i) => i !== index);
    setParticipants(copie.length ? copie : [{ ...participantVide }]);
  };

  const montantTotal = participants.reduce((sum, p) => {
    const t = tarifs.find((x) => x.id === parseInt(p.tarif_id));
    const base = t ? parseFloat(t.montant) : 0;
    const supplement = p.besoin_carte_sejour ? parseFloat(sessionChoisie?.supplement_carte_sejour || 150) : 0;
    return sum + base + supplement;
  }, 0);

  const step1Valide = !!sessionId;
  const step3Valide = participants.every((p) => p.nom && p.prenom && p.tarif_id);

  const handleSubmit = async () => {
    setLoading(true); setError("");
    // Validation des dates d'expiration passeport (annee coherente), affichee sous le champ
    const anneeCourante = new Date().getFullYear();
    const nouvellesErreurs = {};
    participants.forEach((p, idx) => {
      if (p.date_expiration_passeport) {
        const annee = parseInt(String(p.date_expiration_passeport).slice(0, 4), 10);
        if (isNaN(annee) || annee < anneeCourante || annee > 2050) {
          nouvellesErreurs[idx] = `Date d'expiration passeport invalide pour ${p.prenom || ""} ${p.nom || ""} : l'année doit être comprise entre ${anneeCourante} et 2050.`;
        }
      }
    });
    if (Object.keys(nouvellesErreurs).length > 0) {
      setErreursPasseport(nouvellesErreurs);
      setLoading(false);
      return;
    }
    setErreursPasseport({});
    const payload = {
      session_id: parseInt(sessionId),
      type_inscription: typeInscription,
      participants: participants.map((p) => ({
        ...p,
        tarif_id: parseInt(p.tarif_id),
        date_naissance: p.date_naissance || null,
        date_expiration_passeport: p.date_expiration_passeport || null,
      })),
    };
    try {
      let res;
      if (editId) {
        res = await api.put("/oumrah/inscriptions/" + editId, payload);
      } else {
        res = await api.post("/oumrah/inscriptions", payload);
      }
      if (onSaved) onSaved(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur serveur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{editId ? "Modifier l'inscription" : "Nouvelle inscription Oumrah"}</DialogTitle>
      <DialogContent>
        <MDBox mb={3} mt={1}>
          {!editId && (
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
          )}
        </MDBox>

        {(editId || activeStep === 0) && (
          <MDBox>
            {editId && (
              <MDTypography variant="button" fontWeight="bold" color="info" display="block" mb={1}>
                Session
              </MDTypography>
            )}
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id="sess-label">Session Oumrah</InputLabel>
              <Select labelId="sess-label" label="Session Oumrah" sx={{ height: 44 }}
                value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
                {sessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.libelle} ({s.places_restantes} places restantes)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {sessionChoisie && (
              <MDBox mt={2}>
                <MDTypography variant="caption" color="text" display="block">Tarifs disponibles :</MDTypography>
                {tarifs.map((t) => (
                  <MDTypography key={t.id} variant="caption" color="text" display="block">
                    • {t.ville_depart ? t.ville_depart + " - " : ""}{t.libelle} : {fmt(t.montant)} €
                  </MDTypography>
                ))}
              </MDBox>
            )}
          </MDBox>
        )}

        {(editId || activeStep === 1) && (
          <MDBox mt={1}>
            {editId && (
              <MDTypography variant="button" fontWeight="bold" color="info" display="block" mb={1}>
                Type d'inscription
              </MDTypography>
            )}
            <FormControl fullWidth>
              <InputLabel id="type-label">Type d'inscription</InputLabel>
              <Select labelId="type-label" label="Type d'inscription" sx={{ height: 44 }}
                value={typeInscription} onChange={(e) => setTypeInscription(e.target.value)}>
                <MenuItem value="individuel">Individuel</MenuItem>
                <MenuItem value="couple">Couple</MenuItem>
                <MenuItem value="famille">Famille</MenuItem>
              </Select>
            </FormControl>
          </MDBox>
        )}

        {(editId || activeStep === 2) && (
          <MDBox mt={1}>
            {editId && (
              <MDTypography variant="button" fontWeight="bold" color="info" display="block" mb={1}>
                Participants
              </MDTypography>
            )}
            {participants.map((p, i) => (
              <Card key={i} sx={{ p: 2, mb: 2, border: "1px solid #eee" }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <MDTypography variant="button" fontWeight="bold">Participant {i + 1}</MDTypography>
                  <IconButton size="small" onClick={() => retirerParticipant(i)}>
                    <Icon>delete</Icon>
                  </IconButton>
                </MDBox>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField label="Nom" fullWidth size="small" value={p.nom}
                      onChange={(e) => majParticipant(i, "nom", e.target.value)} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Prénom" fullWidth size="small" value={p.prenom}
                      onChange={(e) => majParticipant(i, "prenom", e.target.value)} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Date de naissance" type="date" fullWidth size="small"
                      InputLabelProps={{ shrink: true }} value={p.date_naissance}
                      onChange={(e) => majParticipant(i, "date_naissance", e.target.value)} />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id={"sexe-" + i}>Sexe</InputLabel>
                      <Select labelId={"sexe-" + i} label="Sexe" sx={{ height: 44 }}
                        value={p.sexe} onChange={(e) => majParticipant(i, "sexe", e.target.value)}>
                        <MenuItem value="H">Homme</MenuItem>
                        <MenuItem value="F">Femme</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Nationalité" fullWidth size="small" value={p.nationalite}
                      onChange={(e) => majParticipant(i, "nationalite", e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <MDBox display="flex" flexWrap="wrap" gap={1} alignItems="center">
                      <MDTypography variant="caption" color="text" sx={{ mr: 1 }}>Assistance :</MDTypography>
                      <MDBox display="flex" alignItems="center">
                        <Checkbox checked={!!p.besoin_assistance} onChange={(e) => majParticipant(i, "besoin_assistance", e.target.checked)} size="small" />
                        <MDTypography variant="caption">Besoin d'assistance</MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="center">
                        <Checkbox checked={!!p.fauteuil_roulant} onChange={(e) => majParticipant(i, "fauteuil_roulant", e.target.checked)} size="small" />
                        <MDTypography variant="caption">Fauteuil roulant</MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="center">
                        <Checkbox checked={!!p.malade} onChange={(e) => majParticipant(i, "malade", e.target.checked)} size="small" />
                        <MDTypography variant="caption">Malade</MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="center">
                        <Checkbox checked={!!p.besoin_carte_sejour} onChange={(e) => majParticipant(i, "besoin_carte_sejour", e.target.checked)} size="small" />
                        <MDTypography variant="caption">Le participant possède une carte de séjour (+{fmt(sessionChoisie?.supplement_carte_sejour || 150)} €)</MDTypography>
                      </MDBox>
                    </MDBox>
                  </Grid>
                  {p.malade && (
                    <Grid item xs={12}>
                      <TextField label="Précisions sur la maladie" fullWidth size="small" multiline rows={2}
                        value={p.malade_details || ""}
                        onChange={(e) => majParticipant(i, "malade_details", e.target.value)} />
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <TextField label="N° passeport" fullWidth size="small" value={p.num_passeport}
                      onChange={(e) => majParticipant(i, "num_passeport", e.target.value)}
                      disabled={!(statutInscription === "validee" || statutInscription === "confirmee")} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Expiration passeport" type="date" fullWidth size="small"
                      InputLabelProps={{ shrink: true }} value={p.date_expiration_passeport}
                      inputProps={{ min: new Date().toISOString().slice(0, 10), max: "2050-12-31" }}
                      onChange={(e) => majParticipant(i, "date_expiration_passeport", e.target.value)}
                      disabled={!(statutInscription === "validee" || statutInscription === "confirmee")} />
                    {erreursPasseport[i] && (
                      <MDTypography variant="caption" sx={{ color: "#d32f2f", fontWeight: "bold" }} display="block" mt={0.5}>
                        {erreursPasseport[i]}
                      </MDTypography>
                    )}
                  </Grid>
                  {!(statutInscription === "validee" || statutInscription === "confirmee") && (
                    <Grid item xs={12}>
                      <MDTypography variant="caption" sx={{ color: "#e65100" }}>
                        Les champs passeport ne seront disponibles qu'après validation de l'inscription.
                      </MDTypography>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <TextField label="Téléphone" fullWidth size="small" value={p.telephone}
                      onChange={(e) => majParticipant(i, "telephone", e.target.value)} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Email" fullWidth size="small" value={p.email}
                      onChange={(e) => majParticipant(i, "email", e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel id={"tarif-" + i}>Tarif</InputLabel>
                      <Select labelId={"tarif-" + i} label="Tarif" sx={{ height: 44 }}
                        value={p.tarif_id} onChange={(e) => majParticipant(i, "tarif_id", e.target.value)}>
                        {tarifs.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.ville_depart ? t.ville_depart + " - " : ""}{t.libelle} — {fmt(t.montant)} €
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {(typeInscription === "famille" || typeInscription === "couple") && (
                    <Grid item xs={12}>
                      <MDBox display="flex" alignItems="center">
                        <Checkbox checked={!!p.est_conjoint} onChange={() => toggleConjoint(i)} size="small" />
                        <MDTypography variant="caption" color="text">Époux / Épouse (couple marié)</MDTypography>
                      </MDBox>
                    </Grid>
                  )}
                </Grid>
              </Card>
            ))}
            <MDButton color="info" variant="outlined" size="small" onClick={ajouterParticipant}>
              <Icon>add</Icon>&nbsp;Ajouter une personne
            </MDButton>
          </MDBox>
        )}

        {(editId || activeStep === 3) && (
          <MDBox mt={1}>
            {editId && (
              <MDTypography variant="button" fontWeight="bold" color="info" display="block" mb={1}>
                Récapitulatif
              </MDTypography>
            )}
            <MDTypography variant="button" fontWeight="bold" display="block">
              {sessionChoisie?.libelle}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block" mb={1}>
              Type : <span style={{ textTransform: "capitalize" }}>{typeInscription}</span>
            </MDTypography>
            <Divider sx={{ my: 1 }} />
            {participants.map((p, i) => {
              const t = tarifs.find((x) => x.id === parseInt(p.tarif_id));
              return (
                <MDBox key={i} display="flex" justifyContent="space-between" py={0.5}>
                  <MDTypography variant="caption" color="text">{p.prenom} {p.nom}</MDTypography>
                  <MDTypography variant="caption" color="text">
                    {t ? (t.ville_depart ? t.ville_depart + " - " : "") + t.libelle + " — " + fmt(t.montant) + " €" : "Pas de tarif"}
                  </MDTypography>
                </MDBox>
              );
            })}
            <Divider sx={{ my: 1 }} />
            <MDBox display="flex" justifyContent="space-between">
              <MDTypography variant="button" fontWeight="bold">Total</MDTypography>
              <MDTypography variant="button" fontWeight="bold" color="info">{fmt(montantTotal)} €</MDTypography>
            </MDBox>
            {editId && docsInscription && (
              <MDBox mt={3}>
                <Divider sx={{ my: 2 }} />
                <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                  Documents des participants
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block" mb={2}>
                  Formats acceptés : PDF, JPG, PNG (max 10 Mo)
                </MDTypography>
                {!(statutInscription === "validee" || statutInscription === "confirmee") && (
                  <MDTypography variant="caption" sx={{ color: "#e65100" }} display="block" mb={2}>
                    L'ajout de documents ne sera disponible qu'après validation de l'inscription.
                  </MDTypography>
                )}
                {docsInscription.participants.map((part) => (
                  <MDBox key={part.id} sx={{ bgcolor: "#f8f9fa", borderRadius: 2, p: 2, mb: 2 }}>
                    <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                      {part.prenom} {part.nom}
                    </MDTypography>
                    {part.documents.map((doc) => {
                      const key = part.id + "_" + doc.type_document_id;
                      const enCours = !!uploadEnCours[key];
                      const inputId = "upload-" + key;
                      return (
                        <MDBox key={doc.type_document_id} display="flex" alignItems="center" justifyContent="space-between" py={0.5} sx={{ borderBottom: "1px solid #eee" }}>
                          <MDBox flex={1}>
                            <MDTypography variant="caption" fontWeight="medium" display="block">
                              {doc.libelle}
                              {doc.obligatoire && <span style={{ color: "#e53935" }}> *</span>}
                            </MDTypography>
                            {doc.fichier && (
                              <MDTypography variant="caption" color="text" display="block" sx={{ fontSize: 11 }}>
                                {doc.fichier.nom_original} · {(doc.fichier.taille / 1024).toFixed(0)} Ko
                              </MDTypography>
                            )}
                          </MDBox>
                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            {doc.fichier ? (
                              <>
                                <IconButton size="small" color="info" onClick={() => ouvrirFichier(part.id, doc.type_document_id)} title="Consulter">
                                  <Icon fontSize="small">visibility</Icon>
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => supprimerFichier(part.id, doc.type_document_id)} title="Supprimer">
                                  <Icon fontSize="small">delete</Icon>
                                </IconButton>
                              </>
                            ) : (
                              <>
                                <input
                                  type="file"
                                  id={inputId}
                                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                                  style={{ display: "none" }}
                                  onChange={(e) => {
                                    const f = e.target.files[0];
                                    if (f) uploaderFichier(part.id, doc.type_document_id, f);
                                    e.target.value = "";
                                  }}
                                />
                                <MDButton
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  disabled={enCours || !(statutInscription === "validee" || statutInscription === "confirmee")}
                                  onClick={() => document.getElementById(inputId).click()}
                                >
                                  <Icon fontSize="small">upload_file</Icon>&nbsp;
                                  {enCours ? "Envoi..." : "Ajouter"}
                                </MDButton>
                              </>
                            )}
                          </MDBox>
                        </MDBox>
                      );
                    })}
                  </MDBox>
                ))}
              </MDBox>
            )}
            {error && (
              <MDTypography variant="caption" color="error" display="block" mt={1}>{error}</MDTypography>
            )}
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary">Annuler</MDButton>
        {!editId && activeStep > 0 && (
          <MDButton onClick={handleBack} color="info" variant="outlined">Précédent</MDButton>
        )}
        {!editId && activeStep < steps.length - 1 ? (
          <MDButton onClick={handleNext} color="info"
            disabled={(activeStep === 0 && !step1Valide) || (activeStep === 2 && !step3Valide)}>
            Suivant
          </MDButton>
        ) : (
          <MDButton onClick={handleSubmit} color="info" disabled={loading || !step1Valide || !step3Valide}>
            {loading ? "Envoi..." : editId ? "Enregistrer" : "Valider l'inscription"}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default FormulaireInscriptionOumrah;
